import { endOfDay, format, isEqual, setHours, startOfDay } from "date-fns"
import { Timestamp } from "gen/google/protobuf/timestamp_pb"
import { TimestampRange as TimestampRangePB } from "gen/proto/timestamprange/models_pb"

import { Format } from "../classes/timestamp"

export const DeliveryWindow = {
  MORNING: "Morning",
  MIDDAY: "Midday",
  EVENING: "Evening",
} as const

export type DeliveryWindow =
  | (typeof DeliveryWindow)[keyof typeof DeliveryWindow]
  | null // Null means any time that day.

const deliveryWindowHours: Record<
  NonNullable<DeliveryWindow>,
  [startHour: number, endHour: number]
> = {
  [DeliveryWindow.MORNING]: [6, 10],
  [DeliveryWindow.MIDDAY]: [10, 14],
  [DeliveryWindow.EVENING]: [14, 18],
}

// Null sends a full-day range so the backend doesn't keep the old window.
export function deliveryWindowBounds(
  date: Date,
  window: DeliveryWindow
): [Date, Date] {
  const dayStart = startOfDay(date)
  if (window === null) return [dayStart, endOfDay(dayStart)]
  const [startHour, endHour] = deliveryWindowHours[window]
  return [setHours(dayStart, startHour), setHours(dayStart, endHour)]
}

export function deliveryWindowFromRange(
  range: TimestampRangePB | undefined
): DeliveryWindow {
  if (!range) return null
  if (!range.start || !range.end) {
    throw new Error("Delivery date range is missing a start or end")
  }
  const start = Timestamp.toDate(range.start)
  const end = Timestamp.toDate(range.end)
  return (
    Object.values(DeliveryWindow).find((window) => {
      const [windowStart, windowEnd] = deliveryWindowBounds(start, window)
      return isEqual(windowStart, start) && isEqual(windowEnd, end)
    }) ?? null
  )
}

export function formatRequestedDate(
  date: Date,
  window: DeliveryWindow,
  dateFormat: Format
): string {
  const day = format(date, dateFormat)
  return window ? `${day} · ${window}` : day
}
